/* eslint-disable react/no-array-index-key */
import React, { Fragment, lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import AuthGuard from 'src/components/AuthGuard';
import GuestGuard from 'src/components/GuestGuard';
import LoadingScreen from 'src/components/LoadingScreen';
import DashboardLayoutEnergy from 'src/layouts/DashboardLayoutEnergy';
import MainLayout from 'src/layouts/MainLayout';
import AuthLayout from './submodules/commons-auth';
import systemRoutes from './submodules/commons-system/Routes';

const routesConfig = [
	{
		exact: true,
		path: '/',
		component: () => <Redirect to="/app" />
	},
	{
		exact: true,
		path: '/404',
		component: lazy(() => import('src/views/pages/Error404View'))
	},

	{
		exact: true,
		path: '/500',
		component: lazy(() => import('src/views/pages/Error500'))
	},
	{
		path: '/auth',
		guard: GuestGuard,
		layout: AuthLayout,
		routes: [
			{
				exact: true,
				path: '/auth/mobile',
				component: lazy(() => import('src/submodules/commons-auth/Mobile'))
			},
			{
				exact: true,
				path: '/auth/login',
				component: lazy(() => import('src/submodules/commons-auth/Login'))
			},
			{
				exact: true,
				path: '/auth/login-unprotected',
				component: lazy(() => import('src/submodules/commons-auth/Login'))
			},
			{
				exact: true,
				path: '/auth/registration',
				component: lazy(() => import('src/submodules/commons-auth/Registration'))
			},
			{
				exact: true,
				path: '/auth/complete-registration',
				component: lazy(() => import('src/submodules/commons-auth/CompleteRegistration'))
			},
			{
				exact: true,
				path: '/auth/lostpassword',
				component: lazy(() => import('src/submodules/commons-auth/LostPassword'))
			},
			{
				exact: true,
				path: '/auth/resetpassword',
				component: lazy(() => import('src/submodules/commons-auth/ResetPassword'))
			},
			{
				component: () => <Redirect to="/auth/login" /> 
			}
		]
	},
	{
		path: '/validation',
		routes: [
			{
				exact: true,
				path: '/Validation/EmailConfirm/:token',
				component: lazy(() => import('src/views/validation/EmailConfirm'))
			},
			{
				component: () => <Redirect to="/404" /> 
			}		
		]
	},
	{
		path: '/app',
		guard: AuthGuard,
		layout: DashboardLayoutEnergy,
		routes: [
			{
				exact: true,
				path: '/app',
				component: () => <Redirect to="/app/reports/dashboard" />
			},
			{
				exact: true,
				path: '/app/account',
				component: lazy(() => import('src/views/pages/AccountView'))
			},
			{
				exact: true,
				path: '/app/reports/dashboard',
				component: lazy(() => import('src/views/reportsEnergy/Dashboard'))
			},
			{
				path: '/app/communities',
				routes: [
					{
						exact: true,
						path: '/app/communities',
						component: lazy(() => import('src/views/Communities/List'))
					},
					{
						exact: true,
						path: '/app/communities/:communityId/view',
						component: lazy(() => import('src/views/Communities/Details'))
					},
					{
						exact: true,
						path: [
							'/app/communities/create',
							'/app/communities/:communityId/edit'
						],
						component: lazy(() => import('src/views/Communities/Edit'))
					},
					{
						exact: true,
						path: [
							'/app/communities/:communityId/partecipants/create',
							'/app/communities/:communityId/partecipants/:partecipantId/edit'
						],
						component: lazy(() =>
							import('src/views/Communities/Details/Partecipants/Edit')
						)
					},
					{
						exact: true,
						path: [
							'/app/communities/:communityId/homes/new',
							'/app/communities/:communityId/homes/:homeId'
						],
						component: lazy(() =>
							import('src/views/Communities/Details/Homes/Edit')
						)
					},
					{
						exact: true,
						path: [
							'/app/communities/:communityId/machinery/new',
							'/app/communities/:communityId/machinery/:machineryId'
						],
						component: lazy(() =>
							import('src/views/Communities/Details/Machinery/Edit')
						)
					},
					{
						exact: true,
						path: [
							'/app/communities/:communityId/activity-plans/new',
							'/app/communities/:communityId/activity-plans/:activityPlanId'
						],
						component: lazy(() =>
							import('src/views/Communities/Details/ActivityPlans/Edit')
						)
					},
					{
						component: () => <Redirect to="/400" />
					}
				]
			},
			{
				path: '/app/aggregates',
				routes: [
					{
						exact: true,
						path: '/app/aggregates',
						component: lazy(() => import('src/views/AggregateCommunities/List'))
					},
					{
						exact: true,
						path: '/app/aggregates/:communityId/view',
						component: lazy(() =>
							import('src/views/AggregateCommunities/Details')
						)
					},
					{
						exact: true,
						path: [
							'/app/aggregates/create',
							'/app/aggregates/:communityId/edit'
						],
						component: lazy(() => import('src/views/AggregateCommunities/Edit'))
					},
					{
						component: () => <Redirect to="/400" />
					}
				]
			},
			{
				path: '/app/contracts',
				routes: [
					{
						exact: true,
						path: '/app/contracts',
						component: lazy(() => import('src/views/crm/Contracts/List'))
					},
					{
						exact: true,
						path: [
							'/app/contracts/create',
							'/app/contracts/create/:idCustomer',
							'/app/contracts/:contractId/edit'
						],
						component: lazy(() => import('src/views/crm/Contracts/Edit'))
					},
					{
						exact: true,
						path: ['/app/contracts/:contractId/lines/:lineId/edit'],
						component: lazy(() => import('src/views/crm/Contracts/EditCurve'))
					},
					{
						component: () => <Redirect to="/404" />
					}
				]
			},
			{
				path: '/app/customers',
				routes: [
					{
						exact: true,
						path: '/app/customers',
						component: lazy(() => import('src/submodules/eportal-ui/Customers/List'))
					},
					{
						exact: true,
						path: ['/app/customers/create', '/app/customers/:customerId/edit'],
						component: lazy(() => import('src/submodules/eportal-ui/Customers/Edit'))
					},
					{
						exact: true,
						path: ['/app/customers/:customerId/view'],
						component: lazy(() => import('src/submodules/eportal-ui/Customers/View'))
					},
					{
						component: () => <Redirect to="/404" />
					}
				]
			},
			{
				path: '/app/maps',
				component: lazy(() => import('src/views/Maps'))
			},
			{
				exact: true,
				path: '/app/documents',
				component: lazy(() => import('src/submodules/commons-dms'))
			},
			{				
				path: '/app/management/priceLists',
				routes: [
					{
						exact: true,
						path: '/app/management/priceLists',
						component: lazy(() => import('src/views/crm/PriceList/List'))
					},
					{
						exact: true,
						path: [
							'/app/management/priceLists/create',
							'/app/management/priceLists/:entityId/edit'
						],
						component: lazy(() => import('src/views/crm/PriceList/Edit'))
					},
					{
						component: () => <Redirect to="/400" />
					}
				]
			},
			...systemRoutes,
			{
				component: () => <Redirect to="/404" />
			}
		]
	},
	{
		path: '*',
		layout: MainLayout,
		routes: [
			{
				component: () => <Redirect to="/404" />
			}
		]
	}
];

const renderRoutes = routes =>
	routes ? (
		<Suspense fallback={<LoadingScreen />}>
			<Switch>
				{routes.map((route, i) => {
					const Guard = route.guard || Fragment;
					const Layout = route.layout || Fragment;
					const Component = route.component;

					return (
						<Route
							key={i}
							path={route.path}
							exact={route.exact}
							render={props => (
								<Guard>
									<Layout>
										{route.routes ? (
											renderRoutes(route.routes)
										) : (
											<Component {...props} />
										)}
									</Layout>
								</Guard>
							)}
						/>
					);
				})}
			</Switch>
		</Suspense>
	) : null;

function RoutesEnergy() {
	let rout = renderRoutes(routesConfig);
	return rout;
}

export default RoutesEnergy;

